import React, { useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface LocaleRouterProps {
  children: React.ReactNode;
}

const supportedLocales = ['en', 'es', 'fr', 'de', 'pt', 'vi', 'sv', 'ro'];

const LocaleRouter: React.FC<LocaleRouterProps> = ({ children }) => {
  const { locale } = useParams<{ locale?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();

  // A "locale" segment that isn't a locale is really a page name (e.g. /contact).
  // That is the one case that has to redirect before rendering, otherwise the
  // home page flashes on screen and is then swapped for the real page.
  const isMisroutedPage = locale !== undefined && !supportedLocales.includes(locale);

  useEffect(() => {
    if (locale && supportedLocales.includes(locale)) {
      if (i18n.language !== locale) {
        i18n.changeLanguage(locale);
      }
      return;
    }

    const targetLocale = supportedLocales.includes(i18n.language) ? i18n.language : 'en';

    if (isMisroutedPage) {
      const remainingPath = location.pathname.slice(`/${locale}`.length);
      navigate(
        remainingPath ? `/${targetLocale}${remainingPath}` : `/${targetLocale}/${locale}`,
        { replace: true },
      );
      return;
    }

    // No locale in the URL. The language detector has already resolved one, so
    // the children below are rendering in the right language and the only thing
    // left to do is bring the URL in line with the canonical /{locale}/ form.
    // This runs after the first paint on purpose: making the hero wait for a
    // navigation round trip is what pushed LCP past 2.5s on mobile.
    if (location.pathname === '/' || !location.pathname.startsWith(`/${targetLocale}`)) {
      const newPath =
        location.pathname === '/' ? `/${targetLocale}/` : `/${targetLocale}${location.pathname}`;
      navigate(newPath, { replace: true });
    }
  }, [locale, i18n, navigate, location, isMisroutedPage]);

  if (isMisroutedPage) return null;

  return <>{children}</>;
};

export default LocaleRouter;
